import { feedbackAudioUrl, isCorrectFeedback } from '../feedbackAudio';
import React, { useEffect, useId, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MiniTaskGameHeader from '../components/MiniTaskGameHeader';
import { GardenBackdrop } from '../components/RoundView';
import { withTurkishGenitive } from '../turkishSuffixes';
import { saveSoundHuntProgress, recoverSoundHuntRound, saveTargetSoundHuntProgress, recoverTargetSoundHuntRound } from '../miniTasksApi';
import './soundHunt.css';
import SingleStoreyA from './SingleStoreyA';
import SchoolN from './SchoolN';
import Grade1Letter from './Grade1Letter';
import './letterHunt.css';
import useTourIdleHelp from './useTourIdleHelp';
import { setRoundAudioPlayback } from './pronunciationPlayback';

const INSTRUCTION = 'i_l1_g1_instruction';
const EMPTY_ASSETS = {};

export default function SoundHuntRoundView({ round, boardSessionId, assetUrls = EMPTY_ASSETS, classTitle, soundEnabled = true, onToggleSound, onComplete, onExit, onNextRound, sharedAudioRef, localProgress }) {
  const isN = round.task_type === 'target_sound_hunt';
  const isL4G03 = round.source_level === 4 && round.game_id === 'G03';
  const statusId = useId();
  const [phase, setPhase] = useState('loading');
  const [found, setFound] = useState([]);
  const [highlight, setHighlight] = useState(null);
  const [error, setError] = useState('');
  const [wrongFeedback, setWrongFeedback] = useState(0);
  const [imageError, setImageError] = useState(false);
  const [imageNonce, setImageNonce] = useState(0);
  const [loadedImages, setLoadedImages] = useState([]);
  const localAudioRef = useRef(null);
  const audioRef = sharedAudioRef || localAudioRef;
  const soundEnabledRef = useRef(soundEnabled);
  soundEnabledRef.current = soundEnabled;
  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = !soundEnabled;
  }, [soundEnabled, audioRef]);
  const cancelAudioRef = useRef(null);
  const locked = useRef(true);
  const events = useRef([]);
  const origin = useRef(0);
  const retry = useRef(null);
  const handlers = useRef({});
  useTourIdleHelp(round.v22 && phase === 'ready' && !imageError,
    () => handlers.current.accept('help_prompt'));
  const completeRef = useRef(onComplete);
  completeRef.current = onComplete;

  useEffect(() => {
    const alive = { current: true };
    const instructionIds = round.v22 ? [round.intro_audio_id] : isN ? (round.continuing ? ['i_l1_g4_continue'] : ['i_l1_g4_reminder', 'i_l1_g4_instruction']) : [INSTRUCTION];
    const initialInstructionIds = isN && round.continuing && round.intro_bridge_played ? [] : instructionIds;
    const pendingImages = new Set();
    const prepareImages = () => {
      if (round.items.every((item) => assetUrls[item.image.url])) return Promise.resolve();
      return Promise.all([...round.items, ...(round.reference ? [round.reference] : [])].map((item) => new Promise((resolve, reject) => {
        const img = new Image();
        let timer;
        const clean = () => {
          window.clearTimeout(timer);
          img.onload = null;
          img.onerror = null;
          pendingImages.delete(cancel);
        };
        const fail = () => { clean(); reject(new Error('Resimler yüklenemedi. Yeniden deneyelim.')); };
        const cancel = () => { fail(); img.src = ''; };
        pendingImages.add(cancel);
        img.onload = () => { clean(); resolve(); };
        img.onerror = fail;
        timer = window.setTimeout(fail, 30000);
        img.src = item.image.url;
      })));
    };
    const playOnce = (id) => new Promise((resolve, reject) => {
      if (!alive.current) { reject(new Error('cancelled')); return; }
      const url = feedbackAudioUrl(id, round.audio[id]);
      if (!url) { reject(new Error('Ses kaydı bulunamadı.')); return; }
      // Keep the same gesture-activated media element throughout the task.
      const audio = audioRef.current || new Audio(url);
      audio.preload = 'auto';
      audio.muted = !soundEnabledRef.current;
      audioRef.current = audio;
      audio.src = url;
      setRoundAudioPlayback(audio, round, id);
      let timer, settled = false;
      const clean = () => { window.clearTimeout(timer); audio.onended = null; audio.onerror = null; cancelAudioRef.current = null; };
      const fail = (err) => { if (settled) return; settled = true; clean(); audio.pause(); reject(err); };
      cancelAudioRef.current = () => fail(new Error('cancelled'));
      audio.onended = () => { if (settled) return; settled = true; clean(); resolve(); };
      audio.onerror = () => fail(new Error('Ses oynatılamadı. Yeniden deneyelim.'));
      timer = window.setTimeout(() => fail(new Error('Ses yüklenemedi. Yeniden deneyelim.')), isCorrectFeedback(id) ? 1500 : 30000);
      try { Promise.resolve(audio.play()).catch(fail); } catch (err) { fail(err); }
    });
    const play = (id) => playOnce(id).catch((err) => {
      if (!alive.current) throw err;
      if (isCorrectFeedback(id)) return;
      if (!round.v22) throw err;
      return playOnce(id);
    });
    const sequence = async (ids) => {
      for (const id of ids) {
        if (!alive.current) throw new Error('cancelled');
        const item = round.items.find((candidate) => candidate.audio_id === id);
        setHighlight(item?.id || (round.reference && id === round.intro_audio_id ? 'reference' : null));
        await play(id);
      }
      if (alive.current) setHighlight(null);
    };
    const save = async () => {
      if (round.local_tour) return localProgress(round.game_id, events.current);
      if (round.preview) {
        const selected = events.current.filter((event) => event.kind === 'select')
          .filter((event) => round.items.find((item) => item.id === event.item_id)?.contains_target_sound);
        return { round_completed: selected.length === round.target_count,
          summary: { preview: true, task_type: isN ? 'target_sound_hunt' : 'sound_hunt',
            next_prompt: isN ? 'Harika! Aynı sesi üç sözcükte de buldun.' : 'Harika! Üçünde de a sesini yakaladın!',
            target_taps: selected.length, target_sound: isN ? 'n' : 'a',
            incorrect_taps: events.current.filter((event) => event.kind === 'select'
              && !round.items.find((item) => item.id === event.item_id)?.contains_target_sound).length,
            help_prompt_count: events.current.filter((event) => event.kind === 'help_prompt').length, rounds_remaining: 1 } };
      }
      return (isN ? saveTargetSoundHuntProgress : saveSoundHuntProgress)(round.round_id, boardSessionId, events.current);
    };
    const unlock = () => { locked.current = false; setPhase('ready'); };
    let running = false;
    const run = async (operation) => {
      if (running) return;
      running = true;
      locked.current = true;
      setError('');
      setPhase('playing');
      retry.current = () => run(operation);
      try {
        await operation();
      } catch (err) {
        if (alive.current) {
          setError(err.name === 'NotAllowedError' ? (round.v22 ? 'Ses başlayamadı. Tekrar Dene düğmesine dokun.'
            : 'Ses başlayamadı. Yönergeyi dinleyelim düğmesine yeniden dokun.') : (err.message || 'Yeniden deneyelim.'));
          setPhase('retry');
        }
      } finally {
        running = false;
      }
    };
    const finish = async (summary, nextRound = current.next_round) => {
      setPhase('finishing');
      if (round.v22) {
        await sequence([round.complete_audio_id]);
        if (!alive.current) return;
        if (nextRound && onNextRound) onNextRound(nextRound);
        else completeRef.current(summary);
        return;
      }
      await sequence([isN ? 'f_l1_g4_complete' : 'f_l1_g1_complete']);
      if (!alive.current) return;
      setPhase('bridge');
      await sequence([isN ? 'i_l1_g4_letter_bridge' : 'i_l1_g1_letter_bridge']);
      if (nextRound && onNextRound) {
        if (!isN) await sequence(['i_l1_g1_next_task']);
        if (alive.current) onNextRound(nextRound);
      } else if (alive.current) completeRef.current(summary);
    };
    const intro = async () => {
      await sequence([...initialInstructionIds, ...round.items.map((item) => item.audio_id)]);
      if (!alive.current) return;
      origin.current = Date.now();
      if (!events.current.length) events.current = [{ kind: 'intro_completed', item_id: null, elapsed_ms: 0 }];
      await save();
      if (alive.current) unlock();
    };
    const elapsed = () => Math.max(events.current.at(-1)?.elapsed_ms || 0, Date.now() - origin.current);
    // Freeze each accepted event before I/O. Retrying a failed request/audio never
    // appends the touch again, and rapid touches cannot enter before React renders.
    const accept = (kind, item = null) => {
      if (locked.current) return;
      locked.current = true;
      const selectedIds = events.current.filter((event) => event.kind === 'select')
        .map((event) => event.item_id).filter((id) => round.items.find((candidate) => candidate.id === id)?.contains_target_sound);
      if (kind === 'select' && selectedIds.includes(item.id)) { locked.current = false; return; }
      // Listening is not a new answer. Keep feedback through speech/replay and
      // update it only when an actual, previously unfound answer is selected.
      if (isL4G03 && kind === 'select') {
        setWrongFeedback(count => item.contains_target_sound ? 0 : count + 1);
      }
      if (kind !== 'help_prompt' || !round.unmeasured_help) {
        events.current = [...events.current, { kind, item_id: item?.id || null, elapsed_ms: elapsed() }];
      }
      if (kind === 'select' && item.contains_target_sound) setFound([...selectedIds, item.id]);
      let result;
      let saved = false;
      let namePlayed = false;
      let played = false;
      let helpRecorded = false;
      let helpSaved = false;
      run(async () => {
        if (kind === 'select' && !namePlayed) {
          // The first touch is already frozen as a selection. Start its name
          // immediately while persisting it, without waiting for the network.
          const [savedResult, nameResult] = await Promise.allSettled([
            saved ? Promise.resolve(result) : save(), sequence([item.audio_id]),
          ]);
          if (savedResult.status === 'fulfilled') { result = savedResult.value; saved = true; }
          if (nameResult.status === 'fulfilled') namePlayed = true;
          if (savedResult.status === 'rejected') throw savedResult.reason;
          if (nameResult.status === 'rejected') throw nameResult.reason;
        } else if (!saved) { result = await save(); saved = true; }
        if (!alive.current) return;
        if (kind === 'select' && item.contains_target_sound) {
          const next = [...selectedIds, item.id];
          setFound(next);
          if (!played) {
            const feedback = round.v22 ? [next.length === 1 ? round.first_correct_audio_id : 's_l1_g2_pop'] : isN ? (next.length === 1 ? ['f_l1_g4_first_correct']
              : next.length === round.target_count ? [] : ['s_l1_g2_pop']) : [`f_l1_g1_correct_0${next.length}`];
            await sequence(feedback);
            played = true;
          }
          if (result.round_completed) { await finish(result.summary, result.next_round || current.next_round); return; }
        } else if (!played) {
          const repeatedWrong = round.v22 && kind === 'select'
            && events.current.filter((event) => event.kind === 'select' && event.item_id === item.id).length > 1;
          const ids = kind === 'help_prompt' ? [round.help_audio_id] : kind === 'item_replay' ? [item.audio_id]
            : kind === 'instruction_replay' ? [...instructionIds, ...round.items.map((candidate) => candidate.audio_id)]
            : repeatedWrong ? [] : [round.v22 && round.wrong_audio_id ? round.wrong_audio_id
              : isN ? 'f_l1_g4_no_target' : 'f_l1_g1_no_target'];
          await sequence(ids);
          played = true;
        }
        if (isN && !round.v22 && kind === 'select' && !item.contains_target_sound) {
          if (!helpRecorded) {
            events.current = [...events.current, { kind: 'help_prompt', item_id: item.id, elapsed_ms: elapsed() }];
            helpRecorded = true;
          }
          if (!helpSaved) { await save(); helpSaved = true; }
        }
        if (alive.current) unlock();
      });
    };
    let current = round;
    let introStarted = false;
    const start = () => {
      if (introStarted) return;
      introStarted = true;
      // The first play() runs directly from this button's user gesture.
      run(async () => {
        if (current.status === 'completed') { await finish(current.summary); return; }
        if (current.progress?.intro_completed) {
          await sequence([...initialInstructionIds, ...round.items.map((item) => item.audio_id)]);
          if (alive.current) unlock();
        } else await intro();
      });
    };
    handlers.current = { accept, start };
    let prepared = false;
    run(async () => {
      await prepareImages();
      if (!alive.current) return;
      if (!round.preview && !round.local_tour) current = await (isN ? recoverTargetSoundHuntRound : recoverSoundHuntRound)(round.round_id, boardSessionId);
      if (!alive.current) return;
      events.current = current.progress?.events || [];
      setFound(current.progress?.selected_target_ids || []);
      origin.current = current.progress?.selection_opened_at
        ? Date.parse(current.progress.selection_opened_at)
        : Date.now() - (events.current.at(-1)?.elapsed_ms || 0);
      // Prepare/recover silently, then wait for the one instruction button.
      // A resumed round keeps its choices and timing; this initial replay is
      // not counted as extra instruction or item support.
      setPhase('waiting');
      prepared = true;
    }).then(() => { if (alive.current && prepared && (isN || round.v22) && round.continuing) start(); });
    return () => {
      alive.current = false;
      locked.current = true;
      cancelAudioRef.current?.();
      audioRef.current?.pause();
      [...pendingImages].forEach((cancel) => cancel());
    };
  }, [round, boardSessionId, assetUrls, audioRef, onNextRound, isN, isL4G03, localProgress]);

  const controls = <Box className="sound-hunt-controls">
    <Button className="sound-hunt-control"
      aria-describedby={isL4G03 && (error || imageError) ? statusId : undefined}
      disabled={!['waiting', 'ready', 'retry'].includes(phase) || imageError || loadedImages.length !== round.items.length}
      onClick={() => phase === 'waiting' ? handlers.current.start()
        : phase === 'retry' ? retry.current?.() : handlers.current.accept('instruction_replay')}
      startIcon={<RecordVoiceOverRoundedIcon />}>
      {round.v22 && phase === 'retry' ? 'Tekrar Dene' : 'Yönergeyi dinleyelim'}
    </Button>
    {imageError && <Button className="sound-hunt-start" onClick={() => {
      setImageError(false); setLoadedImages([]); setImageNonce((value) => value + 1);
      if (phase === 'retry') retry.current?.();
    }} startIcon={<ReplayRoundedIcon />}>Resimleri yeniden yükle</Button>}
  </Box>;
  const status = <Typography id={statusId} role="status" aria-live="polite" aria-atomic="true" className="sound-hunt-status">
    {imageError ? 'Bir resim yüklenemedi. Yeniden deneyelim.' : error
      || (isL4G03 && wrongFeedback > 0 && <span key={wrongFeedback} className="letter-hunt-wrong-capsule"><CloseRoundedIcon aria-hidden="true" /> Bir daha deneyelim.</span>)}
  </Typography>;

  return (
    <Box className={`sound-hunt ${isL4G03 ? 'sound-hunt-l4-g03 l4-hunt-feedback' : ''} ${['finishing', 'bridge'].includes(phase) ? 'is-complete' : ''}`}>
      <GardenBackdrop />
      <Box className="sound-hunt-content">
        <MiniTaskGameHeader classTitle={classTitle} soundEnabled={soundEnabled} onToggleSound={onToggleSound} soundRequired={round.v22} />
        <Box className="sound-hunt-header">
          <Button className="sound-hunt-home" onClick={onExit} startIcon={<ArrowBackRoundedIcon />}>Ana sayfa</Button>
          <Typography component="h1" className="sound-hunt-title">{withTurkishGenitive(round.student?.display_name || 'Öğrenci')} Mini Görevi</Typography>
          <Box aria-label={`${found.length} hedef bulundu`} className="sound-hunt-progress">
            {[0, 1, 2].map((index) => <span key={index} className={index < found.length ? 'found' : ''}>●</span>)}
          </Box>
        </Box>
        <Box className="sound-hunt-bottom">
          <Typography className="sound-hunt-badge">{round.badge_title || (isN ? (round.v22 ? '⭐ N Sesini Yakala' : '⭐ N Sesini Bul') : '⭐ A Sesini Yakala')}</Typography>
          {round.reference && <Box className={`sound-hunt-reference ${highlight === 'reference' ? 'is-speaking' : ''}`} aria-label="Referans görseli">
            <VolumeUpRoundedIcon aria-hidden="true" />
            <img key={imageNonce} src={round.reference.image.url} alt="" draggable={false} onError={() => setImageError(true)} />
          </Box>}
          {phase === 'bridge' && <Box className="sound-hunt-letter" aria-label={isN ? 'Yeni sesin harfi' : 'a harfi'}>
            {round.target_sound && round.v22 ? <Grade1Letter letter={round.target_sound} /> : isN ? <SchoolN /> : <SingleStoreyA />}</Box>}
          {isL4G03 && <>{controls}{status}</>}
          <Box className="sound-hunt-cards">
            {round.items.map((item, index) => (
              <Box key={item.id} className={`sound-hunt-card ${found.includes(item.id) ? 'is-found' : ''} ${highlight === item.id ? 'is-speaking' : ''}`}>
                <button type="button" aria-label={`${index + 1}. resmi seç`} className="sound-hunt-picture"
                  disabled={phase !== 'ready' || imageError || loadedImages.length !== round.items.length || found.includes(item.id)}
                  onClick={() => handlers.current.accept('select', item)}>
                  <img key={imageNonce} src={assetUrls[item.image.url] || item.image.url} alt="" draggable={false}
                    onLoad={() => setLoadedImages((ids) => ids.includes(item.id) ? ids : [...ids, item.id])}
                    onError={() => setImageError(true)} />
                  {found.includes(item.id) && <span className="sound-hunt-check" aria-hidden="true">✓</span>}
                  {isN && !round.v22 && <span className="sound-hunt-speaker" aria-hidden="true"><VolumeUpRoundedIcon /></span>}
                </button>
                {(!isN || round.v22) && <Button aria-label={`${index + 1}. resmin adını dinle`} className="sound-hunt-speaker" disabled={phase !== 'ready' || imageError}
                  onClick={() => handlers.current.accept('item_replay', item)}><VolumeUpRoundedIcon /></Button>}
              </Box>
            ))}
          </Box>
          {!isL4G03 && <>{controls}{status}</>}
        </Box>
      </Box>
    </Box>
  );
}
